/**
 * The one translation between the editor's box and the box the drawing contract is written in.
 *
 * Two different languages, both written down, so this is a translation and not a convention:
 *
 *   - the EDITOR's layer is normalised and ANCHORED. `boxGeometry` (`lib/render/engine.ts`) reads
 *     `x`,`y` as a point in 0..1 of the canvas and `width` in 0..1 of the canvas WIDTH; for a QR or
 *     an image that point is the box's centre and an absent `height` means «square» (`h = w`, in
 *     pixels). For TEXT there is no declared box at all: `fitText` uses `width × canvasWidth` as the
 *     maximum line width, `maxLines` caps the lines, and `textGeometry` draws the block anchored on
 *     the point — `align` decides which side of it the line sits, `verticalAlign` which side the
 *     block sits. Point size is `fontSizeRatio × canvasWidth` (`sizePx`), the minimum the same over
 *     `minFontSizeRatio`, and letter spacing is a PERCENTAGE of the point size (`spacingPx`).
 *   - the PLAN's placement is absolute and TOP-LEFT. `resolveRenderPlan` requires finite,
 *     non-negative `x`,`y`,`w`,`h` in canvas units, checks `x + w` and `y + h` against the canvas,
 *     reads `fontSize` from placement, and `weight` / `fontStyle` / `align` / `overflow` /
 *     `maxLines` / `lineHeight` / `letterSpacing` / `minFontSize` from style — with letter spacing
 *     in PIXELS and overflow one of `fail` / `shrink` / `wrap`.
 *
 * Untranslated, every layer this product wrote answered `invalid_layer_geometry`: `w` and `h` were
 * simply not there. So a template could be saved, read back identically and published, and still be
 * impossible to DRAW — none of those three steps runs the planner, and issuance is the first that
 * does. That is the whole reason this module exists.
 *
 * Nothing here is invented. A text layer's allowance is the width the editor fits within and the
 * block height the editor would draw at full size (`maxLines × lineHeight × fontSize`, the
 * reference's own `blockH`), which is never smaller than the plan's own `heightOf`. A layer with no
 * width of its own gets `makeLayer`'s own default. And the plan still refuses a box that leaves the
 * canvas rather than being nudged back onto it.
 */
import type { LayerDefinition } from '../../api/types';
import { V07_LAYOUT_KEY } from './from-api';

export interface RenderCanvas {
  width: number;
  height: number;
}

/** `fitMode` ↔ the plan's `overflow`. «Sabit punto» is a size that must fit as it is: `fail`. */
const OVERFLOW_OF: Record<string, string> = { shrink: 'shrink', wrap: 'wrap', fixed: 'fail' };
const FIT_MODE_OF: Record<string, string> = { shrink: 'shrink', wrap: 'wrap', fail: 'fixed' };

/** The editor's four kinds onto the contract's three. A static text is text; an image is an asset. */
const LAYER_TYPE_OF: Record<string, LayerDefinition['layerType']> = {
  text: 'text',
  static: 'text',
  qr: 'qr',
  image: 'asset',
};

const finite = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback;

const asRecord = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : {};

const canvasOf = (c: RenderCanvas | undefined): RenderCanvas => ({
  width: Math.max(1, finite(c?.width, 1200)),
  height: Math.max(1, finite(c?.height, 1200)),
});

const alignOf = (v: unknown): 'left' | 'center' | 'right' => (v === 'center' || v === 'right' ? v : 'left');

/** `sizePx` and `minPx` — both ratios of the canvas WIDTH, both floored at 4. */
const pointSize = (layer: Record<string, unknown>, W: number): number =>
  Math.max(4, Math.round(finite(layer.fontSizeRatio, 0.04) * W));

const minPointSize = (layer: Record<string, unknown>, W: number): number =>
  Math.max(4, Math.round(finite(layer.minFontSizeRatio, finite(layer.fontSizeRatio, 0.04) * 0.6) * W));

/**
 * A layer, into the contract's own objects: canonical in `placement` and `style`, the editor's own
 * layer beside them.
 *
 * `placement` and `style` are NOT free-form — the render plan reads both, which is what an earlier
 * note in `to-api.ts` got wrong. So each carries exactly what the plan reads, and the editor's own
 * layer rides under the same `v07` namespace the template model already uses, where `layerFromApi`
 * picks it up unchanged. The validated columns are copied into that block for the reason they always
 * were: it is how `null` and ABSENT stay different across a save.
 */
export function layerToApi(layer: Record<string, unknown>, canvas?: RenderCanvas): Record<string, unknown> {
  const { width: W, height: H } = canvasOf(canvas);
  const kind = String(layer.kind ?? 'text');
  const isText = kind === 'text' || kind === 'static';

  const w = finite(layer.width, 0.1) * W;
  const anchorX = finite(layer.x, 0.5) * W;
  const anchorY = finite(layer.y, 0.5) * H;
  const style: Record<string, unknown> = {};
  let x: number;
  let y: number;
  let h: number;

  if (isText) {
    const fontSize = pointSize(layer, W);
    const maxLines = Math.max(1, Math.round(finite(layer.maxLines, 1)));
    const lineHeight = finite(layer.lineHeight, 1.15);
    const align = alignOf(layer.align);
    /* `blockH` — the reference's own: every line a full line box, at the layer's own point size. */
    h = maxLines * lineHeight * fontSize;
    /* `textGeometry`: the point is an ANCHOR and `align` says which side of it the line sits. */
    x = align === 'left' ? anchorX : align === 'right' ? anchorX - w : anchorX - w / 2;
    const vAlign = layer.verticalAlign;
    y = vAlign === 'top' ? anchorY : vAlign === 'bottom' ? anchorY - h : anchorY - h / 2;
    style.fontSize = fontSize;
    style.minFontSize = minPointSize(layer, W);
    style.weight = finite(layer.weight, 400);
    /* The editor's `style` IS the CSS font-style; the plan reads it under `fontStyle`. */
    style.fontStyle = layer.style === 'italic' ? 'italic' : 'normal';
    style.align = align;
    /*
     * Which end of the box the fitted block sits at.
     *
     * `h` above is the allowance — every permitted line, at full point size — and `fitText` needs it
     * to decide whether the text fits at all. It is NOT where the words go: `textGeometry` puts a
     * block of the ACTUAL line count at the FITTED size on the anchor, and `y` above is derived so
     * that the box's top, middle or bottom IS that anchor. Without this the renderer had nothing to
     * position by and drew every block against the top of a two-line box, so a one-line value, or
     * any value that shrank to fit, floated visibly above where the author put it.
     */
    style.verticalAlign =
      layer.verticalAlign === 'top' || layer.verticalAlign === 'bottom' ? layer.verticalAlign : 'middle';
    style.overflow = OVERFLOW_OF[String(layer.fitMode ?? 'shrink')] ?? 'shrink';
    style.maxLines = maxLines;
    style.lineHeight = lineHeight;
    /* The editor's letter spacing is a percentage of the point size; the plan's is pixels. */
    style.letterSpacing = (finite(layer.letterSpacing, 0) / 100) * fontSize;
  } else {
    /* `boxGeometry`: the point is the box's CENTRE, and an absent height means a square box. */
    h = layer.height === null || layer.height === undefined ? w : finite(layer.height, 0) * H;
    x = anchorX - w / 2;
    y = anchorY - h / 2;
  }

  if (typeof layer.color === 'string') style.color = layer.color;
  if (typeof layer.imageFit === 'string') style.imageFit = layer.imageFit;

  /*
   * The presentation the author actually set, in the canonical block the drawing reads.
   *
   * Every one of these is a control the editor offers — the layer list's «Gizle» toggle, the
   * property panel's «Büyük harf / Küçük harf», «Yerleşim», «Döndür», «Opaklık» and the crop
   * rectangle — and none of them reached the production planner, which reads `style` and nothing
   * else. So a hidden layer was drawn, an uppercase layer was drawn in its original case, a «Doldur»
   * image was drawn contained, and opacity and rotation were dropped. `v07` below carries the same
   * values for the editor's own round trip; these are the copies the renderer reads.
   */
  if (layer.hidden === true) style.hidden = true;
  const opacity = finite(layer.opacity, 1);
  if (opacity < 1) style.opacity = Math.max(0, opacity);
  const rotation = finite(layer.rotation, 0);
  if (rotation !== 0) style.rotation = rotation;
  if (isText && typeof layer.textTransform === 'string' && layer.textTransform !== 'none')
    style.textTransform = layer.textTransform;
  /* «Sabit metin» — the literal the author typed, which has no field to be read from. */
  if (kind === 'static' && typeof layer.text === 'string') style.text = layer.text;
  /*
   * The same literal in the other sealed language, keyed by language.
   *
   * Written beside the literal rather than instead of it: `style.text` stays the words the release
   * declares itself to be in, and this is what a document sealed in another language draws in its
   * place. A language absent here is a language the release cannot draw this layer in, which the
   * publish gate refuses by name rather than silently drawing the literal twice.
   */
  if (kind === 'static' && layer.textLocalized !== null && typeof layer.textLocalized === 'object') {
    const bag: Record<string, string> = {};
    for (const locale of ['tr', 'en'] as const) {
      const v = (layer.textLocalized as Record<string, unknown>)[locale];
      if (typeof v === 'string' && v.trim() !== '') bag[locale] = v;
    }
    if (Object.keys(bag).length > 0) style.textLocalized = bag;
  }
  /*
   * The rest of what the placement solver reads, for an image layer.
   *
   * `crop` is «Kırp»'s own rectangle; `scale` is what «Ölçek» multiplies the source size by; `focal`
   * is the point «Doldur» keeps in view. None of them has a `preserveAspectRatio` equivalent, which
   * is why the renderer used to lose them — it now runs the same solver the editor does.
   */
  if (!isText) {
    if (layer.crop && typeof layer.crop === 'object')
      style.crop = { ...(layer.crop as Record<string, unknown>) };
    if (typeof layer.scale === 'number' && Number.isFinite(layer.scale)) style.scale = layer.scale;
    if (layer.focal && typeof layer.focal === 'object')
      style.focal = { ...(layer.focal as Record<string, unknown>) };
  }

  const placement: Record<string, unknown> = { x, y, w, h };
  if (isText) placement.fontSize = style.fontSize;

  /* An image layer names its asset through `assetRef`; the contract has two columns for it. */
  const ref = asRecord(layer.assetRef);
  const assetId = (layer.assetId as string | null) ?? (ref.assetId as string | undefined) ?? null;
  const assetSha = (layer.assetSha256 as string | null) ?? (ref.sha256 as string | undefined) ?? null;

  return {
    layerKey: String(layer.id ?? ''),
    layerType: LAYER_TYPE_OF[kind] ?? 'text',
    fieldKey: (layer.fieldKey as string | null) ?? null,
    assetId,
    assetSha256: assetSha,
    fontFamily: (layer.font as string | null) ?? null,
    zIndex: typeof layer.zIndex === 'number' ? (layer.zIndex as number) : 0,
    placement,
    style: { ...style, [V07_LAYOUT_KEY]: { ...layer } },
  };
}

/**
 * The same translation backwards, from two sources in this order.
 *
 * A layer this product wrote carries its own under `style.v07` and comes back exactly as it went. A
 * layer the product did NOT write — a seeded template, an integration — carries only canonical
 * placement, and is converted so the editor can open and draw it. Spreading the canonical values
 * straight in is what used to put `x: 100` where the editor means a ratio, and the property panel
 * then showed «10000.0 %».
 *
 * A text layer read back this way gets `height: null`, because the editor's model has no declared
 * text box: its allowance is the fit width, the line count and the point size, which are all read.
 */
export function layerFromApi(l: LayerDefinition, canvas?: RenderCanvas): Record<string, unknown> {
  const style = asRecord(l.style);
  const own = style[V07_LAYOUT_KEY];
  if (own && typeof own === 'object') {
    const out = { ...(own as Record<string, unknown>) };
    /* Presence from the stored copy, the authoritative value from the validated column. */
    if (l.fieldKey !== null && l.fieldKey !== undefined) out.fieldKey = l.fieldKey;
    if (l.assetSha256 !== null && l.assetSha256 !== undefined && out.assetId !== undefined)
      out.assetSha256 = l.assetSha256;
    if (l.assetId !== null && l.assetId !== undefined && out.assetId !== undefined) out.assetId = l.assetId;
    if (l.fontFamily !== null && l.fontFamily !== undefined) out.font = l.fontFamily;
    /*
     * The case fold's language, pinned into the canonical style when the release was published.
     *
     * It belongs in this list for the same reason the four above do: the editor's stored copy is
     * what the author last typed, and the canonical column is what the server actually drew from.
     * A released layer whose fold language is not carried across here would preview in one language
     * and print in the other. Absent means Turkish on both sides, which is what every release
     * published before this change carries.
     */
    if (style.textTransformLocale !== undefined) out.textTransformLocale = style.textTransformLocale;
    out.id = l.layerKey;
    out.zIndex = l.zIndex;
    return out;
  }

  const { width: W, height: H } = canvasOf(canvas);
  const placement = asRecord(l.placement);
  const w = finite(placement.w, 0.1 * W);
  const h = finite(placement.h, w);
  const fontSize = finite(placement.fontSize, Math.round(0.04 * W));
  const align = alignOf(style.align);
  const left = finite(placement.x, 0);
  const top = finite(placement.y, 0);
  const isText = l.layerType === 'text';
  /* The inverse of the anchoring above: text anchors by `align`, everything else by its centre. */
  const anchorX = isText
    ? align === 'left'
      ? left
      : align === 'right'
        ? left + w
        : left + w / 2
    : left + w / 2;
  const anchorY = isText ? top + h / 2 : top + h / 2;

  const out: Record<string, unknown> = {
    id: l.layerKey,
    kind: l.layerType === 'asset' ? 'image' : l.layerType,
    fieldKey: l.fieldKey ?? '',
    x: anchorX / W,
    y: anchorY / H,
    width: w / W,
    height: isText ? null : h / H,
    rotation: finite(style.rotation, 0),
    zIndex: l.zIndex,
    opacity: finite(style.opacity, 1),
    hidden: style.hidden === true,
  };
  if (isText) {
    Object.assign(out, {
      text: typeof style.text === 'string' ? style.text : '',
      /* Read back so the editor's own boxes hold what was stored, per language. */
      textLocalized:
        style.textLocalized !== null && typeof style.textLocalized === 'object'
          ? { ...(style.textLocalized as Record<string, unknown>) }
          : {},
      font: l.fontFamily ?? 'Poppins',
      weight: finite(style.weight, 600),
      style: style.fontStyle === 'italic' ? 'italic' : 'normal',
      fontSizeRatio: fontSize / W,
      minFontSizeRatio: finite(style.minFontSize, fontSize * 0.6) / W,
      color: typeof style.color === 'string' ? style.color : '#000000',
      align,
      verticalAlign:
        style.verticalAlign === 'top' || style.verticalAlign === 'bottom' ? style.verticalAlign : 'middle',
      lineHeight: finite(style.lineHeight, 1.15),
      letterSpacing: (finite(style.letterSpacing, 0) / Math.max(1, fontSize)) * 100,
      maxLines: Math.max(1, Math.round(finite(style.maxLines, 1))),
      fitMode: FIT_MODE_OF[String(style.overflow ?? 'shrink')] ?? 'shrink',
      textTransform: 'none',
    });
  }
  if (l.layerType === 'qr') out.color = typeof style.color === 'string' ? style.color : '#000000';
  if (l.layerType === 'asset') {
    out.assetRef = l.assetId && l.assetSha256 ? { assetId: l.assetId, sha256: l.assetSha256 } : null;
    out.imageFit = typeof style.imageFit === 'string' ? style.imageFit : 'contain';
    out.crop = null;
  }
  return out;
}
